import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

/**
 * Story 121.8 — wires the previously-dormant `/api/v1/verticals/tiles`
 * endpoint (V108 / story 102.3) into the dashboard. Each tenant's
 * vertical (e.g. "international_education") seeds a list of tiles in
 * `vertical_dashboard_tile`; the endpoint returns them ordered by
 * `sortOrder`. The dashboard renders them via [VerticalTileGrid].
 *
 * Tenants without a vertical assigned get an empty list and the grid
 * silently hides itself.
 *
 * Shape mirrors `ai.lopettia.kairos.platform.contracts.vertical.
 * VerticalDashboardTile` (the canonical type now in `platform-core`,
 * extracted in 120.2). The `metricQuery` JSON is opaque here; a
 * generic metric-resolver endpoint is a follow-up — for now tiles
 * render the title + the displayType label as a placeholder.
 */
export interface VerticalTileDto {
  id: string;
  tileKey: string;
  titleI18nKey: string;
  displayType: string;
  metricQuery: Record<string, unknown>;
}

export function useVerticalTiles() {
  const tokens = useAuthStore((s) => s.tokens);
  const [tiles, setTiles] = useState<VerticalTileDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tokens) {
      setTiles([]);
      return;
    }
    setLoading(true);
    apiClient
      .get<VerticalTileDto[]>('/verticals/tiles')
      .then(({ data }) => setTiles(data))
      .catch(() => setTiles([]))
      .finally(() => setLoading(false));
  }, [tokens]);

  return { tiles, loading };
}
